import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  FiMessageSquare,
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
  FiCopy,
  FiTrash2,
  FiPlus,
  FiX,
  FiZap,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiEye,
} from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { usePermission } from "../../context/PermissionContext";
import { COUNTRIES, countryName } from "../../utils/countries";
import { copyText } from "../../utils/adsNetworkApi";
import PhoneUserPanel from "./PhoneUserPanel";
import {
  LIMIT_FIELDS,
  PROVIDERS,
  clearProviderCredentials,
  createInboundNumber,
  deleteInboundNumber,
  fetchPhoneUsers,
  fetchSmsConfig,
  fetchWebhookEvents,
  formatDuration,
  providerLabel,
  reasonLabel,
  saveProviderCredentials,
  saveSmsSettings,
  testProvider,
  importProviderNumbers,
  updateInboundNumber,
} from "../../utils/smsVerificationApi";

/**
 * SMS Verification — KYC step 4, set up in one place.
 *
 * The user sends a one-time code by SMS from their own phone to one of our
 * numbers; Twilio / Telnyx call our webhook and the backend checks the code and
 * the sender. Nothing here sends an SMS.
 *
 * Configuration: the master switch (refused until the setup is complete),
 * provider credentials (super admin only, write-only), inbound numbers,
 * countries, and the attempt limits / blocks. Activity: the webhook log and
 * blocked / verified users.
 */

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const Card = ({ title, subtitle, children, actions }) => (
  <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <h2 className="text-sm font-bold text-gray-800">{title}</h2>
        {subtitle && <p className="text-[11px] text-gray-400 mt-0.5 max-w-3xl">{subtitle}</p>}
      </div>
      {actions}
    </div>
    {children}
  </section>
);

const Toggle = ({ checked, onChange, label, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition disabled:opacity-40 ${checked ? "bg-emerald-500" : "bg-gray-300"}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
  </button>
);

const inputCls = "w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-100";
const primaryBtn = "px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-40";

const CopyButton = ({ text, label }) => (
  <button
    type="button"
    onClick={async () => ((await copyText(text)) ? toast.success("Copied") : toast.error("Could not copy"))}
    className="p-1.5 text-gray-400 hover:text-gray-700"
    aria-label={label}
    title="Copy"
  >
    <FiCopy size={12} />
  </button>
);

/** Countries as removable chips plus a picker. */
const CountryPicker = ({ value, onChange, disabled, label }) => {
  const [pick, setPick] = useState("");
  const available = COUNTRIES.filter((c) => !value.includes(c.code));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.length === 0 && <span className="text-[11px] text-gray-400">None</span>}
        {value.map((code) => (
          <span key={code} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-[11px] font-medium">
            {countryName(code)}
            {!disabled && (
              <button type="button" onClick={() => onChange(value.filter((c) => c !== code))} aria-label={`Remove ${countryName(code)}`}>
                <FiX size={11} />
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <select
          aria-label={label}
          value={pick}
          onChange={(e) => {
            if (e.target.value) onChange([...value, e.target.value].sort());
            setPick("");
          }}
          className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs"
        >
          <option value="">+ Add country</option>
          {available.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ status

const StatusCard = ({ config, canEdit, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const enabled = config.settings.isEnabled;
  const toggle = async (next) => {
    setBusy(true);
    try {
      await saveSmsSettings({ isEnabled: next });
      toast.success(next ? "Phone verification switched on" : "Phone verification switched off");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  const s = config.stats;
  return (
    <Card
      title="Phone verification"
      subtitle="When on, users in the selected countries who finished KYC step 3 can verify their number by sending a code from their phone."
      actions={
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold ${enabled ? "text-emerald-600" : "text-gray-400"}`}>{enabled ? "ON" : "OFF"}</span>
          <Toggle checked={enabled} onChange={toggle} disabled={!canEdit || busy} label="Phone verification on" />
        </div>
      }
    >
      <div className="grid sm:grid-cols-2 gap-2">
        {config.readiness.map((c) => (
          <div key={c.key} className="flex items-center gap-2 text-xs" data-testid={`readiness-${c.key}`}>
            {c.ok ? <FiCheckCircle className="text-emerald-500 shrink-0" /> : <FiXCircle className="text-red-500 shrink-0" />}
            <span className={c.ok ? "text-gray-600" : "text-red-600"}>{c.message}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Codes waiting", s.pendingCodes],
          ["Verified (24h)", s.verifiedLast24h],
          ["Verified total", s.verifiedTotal],
          ["Blocked users", s.blockedUsers],
          ["Need review", s.reviewUsers],
        ].map(([label, value]) => (
          <div key={label} className="bg-gray-50 rounded-xl px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
            <div className="text-lg font-bold text-gray-800">{value}</div>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-gray-400">
        Inbound SMS (24h): {s.eventsLast24h.processed} verified · {s.eventsLast24h.rejected} rejected · {s.eventsLast24h.ignored} ignored
      </div>
    </Card>
  );
};

// ------------------------------------------------------------------ general

const GeneralCard = ({ config, canEdit, onSaved }) => {
  const initial = useMemo(
    () => ({
      publicBaseUrl: config.settings.publicBaseUrl || "",
      activeProvider: config.settings.activeProvider || "",
      requireCountryMatch: config.settings.requireCountryMatch,
      mobileNumbersOnly: config.settings.mobileNumbersOnly,
    }),
    [config]
  );
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true);
    try {
      await saveSmsSettings({ ...form, activeProvider: form.activeProvider || null, publicBaseUrl: form.publicBaseUrl.trim() || null });
      toast.success("Settings saved");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="General" subtitle="The public URL must be exactly what Twilio calls — the webhook URLs below are built from it.">
      <div className="grid md:grid-cols-2 gap-4">
        <label className="space-y-1 text-xs">
          <span className="font-medium text-gray-600">Public API URL</span>
          <input
            className={inputCls}
            value={form.publicBaseUrl}
            disabled={!canEdit}
            placeholder="https://api.pxmine.com"
            onChange={(e) => setForm({ ...form, publicBaseUrl: e.target.value })}
          />
        </label>
        <label className="space-y-1 text-xs">
          <span className="font-medium text-gray-600">New codes go to</span>
          <select className={inputCls} value={form.activeProvider} disabled={!canEdit} onChange={(e) => setForm({ ...form, activeProvider: e.target.value })}>
            <option value="">Any provider's numbers</option>
            {PROVIDERS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label} numbers only
              </option>
            ))}
          </select>
          <span className="text-[10px] text-gray-400">Only affects new codes. Codes already issued keep working on their number.</span>
        </label>
        <div className="flex items-center justify-between gap-3 text-xs">
          <span className="text-gray-600">Phone number must be from the user's profile country</span>
          <Toggle checked={form.requireCountryMatch} disabled={!canEdit} label="Require country match" onChange={(v) => setForm({ ...form, requireCountryMatch: v })} />
        </div>
        <div className="flex items-center justify-between gap-3 text-xs">
          <span className="text-gray-600">Mobile numbers only (no landline / VoIP / toll-free)</span>
          <Toggle checked={form.mobileNumbersOnly} disabled={!canEdit} label="Mobile numbers only" onChange={(v) => setForm({ ...form, mobileNumbersOnly: v })} />
        </div>
      </div>
      {canEdit && (
        <div className="flex justify-end">
          <button className={primaryBtn} disabled={!dirty || saving} onClick={save}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </Card>
  );
};

// ------------------------------------------------------------------ providers

const ProviderCard = ({ provider, isSuper, canEdit, onSaved }) => {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);
  const dirty = Object.values(values).some((v) => v && v.trim());

  const save = async () => {
    setSaving(true);
    try {
      // The backend also imports the account's numbers; its message says how many.
      const res = await saveProviderCredentials(provider.provider, values);
      toast.success(res.message || `${providerLabel(provider.provider)} credentials saved`);
      setValues({});
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Remove the ${providerLabel(provider.provider)} credentials? Its webhook will refuse every message until they are saved again.`)) return;
    try {
      await clearProviderCredentials(provider.provider);
      toast.success("Credentials removed");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    }
  };
  const runTest = async () => {
    setTesting(true);
    try {
      const res = await testProvider(provider.provider);
      setTest(res.data);
      toast.success(res.message);
    } catch (err) {
      setTest(err.response?.data || { ok: false, message: err.message });
      toast.error(err.message);
    } finally {
      setTesting(false);
      onSaved();
    }
  };

  return (
    <div className="border border-gray-100 rounded-2xl p-4 space-y-3" data-testid={`provider-${provider.provider}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-gray-800">{providerLabel(provider.provider)}</div>
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
            provider.configured ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"
          }`}
        >
          {provider.configured ? "Configured" : provider.unreadable ? "Unreadable — re-enter" : "Not set"}
        </span>
      </div>

      <div className="space-y-2">
        {provider.fields.map((f) => (
          <label key={f.key} className="block space-y-1 text-xs">
            <span className="font-medium text-gray-600">
              {f.label}
              {!f.required && <span className="text-gray-400"> (optional)</span>}
            </span>
            {isSuper ? (
              <input
                className={inputCls}
                type={f.secret ? "password" : "text"}
                autoComplete="off"
                aria-label={`${providerLabel(provider.provider)} ${f.label}`}
                placeholder={f.hint ? `${f.hint} — leave empty to keep` : f.label}
                value={values[f.key] || ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              />
            ) : (
              <div className="px-3 py-2 bg-gray-50 rounded-xl text-gray-600">{f.hint || "—"}</div>
            )}
          </label>
        ))}
        {!isSuper && <p className="text-[10px] text-gray-400">Only a super admin can change credentials.</p>}
      </div>

      <div className="text-xs space-y-1">
        <div className="flex items-center gap-1">
          <span className="text-gray-400">Webhook URL:</span>
          {provider.webhookUrl ? (
            <>
              <code className="text-[11px] text-gray-700 break-all">{provider.webhookUrl}</code>
              <CopyButton text={provider.webhookUrl} label={`Copy ${provider.provider} webhook URL`} />
            </>
          ) : (
            <span className="text-red-500">set the Public API URL first</span>
          )}
        </div>
        <div className="text-gray-400">
          Last SMS received: <span className="text-gray-600">{formatDate(provider.lastEventAt)}</span>
        </div>
        <div className="text-gray-400">
          Signature failures: <span className={provider.signatureFailures ? "text-red-600 font-semibold" : "text-gray-600"}>{provider.signatureFailures}</span>
          {provider.lastSignatureFailureAt && <span> (last {formatDate(provider.lastSignatureFailureAt)})</span>}
        </div>
        {provider.lastTestedAt && (
          <div className="text-gray-400">
            Last test: <span className={provider.lastTestStatus === "ok" ? "text-emerald-600" : "text-red-600"}>{provider.lastTestMessage}</span> ·{" "}
            {formatDate(provider.lastTestedAt)}
          </div>
        )}
      </div>

      {test && test.numbers && test.numbers.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-3 space-y-1 text-[11px]">
          {test.numbers.map((n) => (
            <div key={n.phoneNumber} className="flex flex-wrap gap-x-3">
              <span className="font-semibold text-gray-700">{n.phoneNumber}</span>
              <span className={n.found ? "text-emerald-600" : "text-red-600"}>{n.found === null ? "not checked" : n.found ? "on account" : "not on this account"}</span>
              {"smsCapable" in n && <span className={n.smsCapable ? "text-emerald-600" : "text-red-600"}>{n.smsCapable ? "SMS capable" : "no SMS"}</span>}
              {"webhookMatches" in n && (
                <span className={n.webhookMatches ? "text-emerald-600" : "text-red-600"}>
                  {n.webhookMatches ? "webhook OK" : `webhook is ${n.webhookUrl || "not set"}`}
                </span>
              )}
              {"messagingProfile" in n && <span className={n.messagingProfile ? "text-emerald-600" : "text-red-600"}>{n.messagingProfile ? "messaging profile OK" : "no messaging profile"}</span>}
              {n.error && <span className="text-red-600">{n.error}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        {isSuper && provider.configured && (
          <button onClick={remove} className="px-3 py-2 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl">
            Remove
          </button>
        )}
        {canEdit && provider.configured && (
          <button onClick={runTest} disabled={testing} className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center gap-1 disabled:opacity-40">
            <FiZap size={12} /> {testing ? "Testing…" : "Test connection"}
          </button>
        )}
        {isSuper && (
          <button onClick={save} disabled={!dirty || saving} className={primaryBtn}>
            {saving ? "Saving…" : "Save credentials"}
          </button>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ numbers

const EMPTY_NUMBER = { provider: "twilio", phoneNumber: "", label: "", countries: [] };

const NumbersCard = ({ numbers, providers, canCreate, canEdit, canDelete, onSaved }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_NUMBER);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const configured = providers.filter((p) => p.configured);

  // Numbers bought after the credentials were saved: pull them in from every configured provider.
  const importAll = async () => {
    setImporting(true);
    try {
      for (const p of configured) {
        try {
          const res = await importProviderNumbers(p.provider);
          toast.success(`${providerLabel(p.provider)}: ${res.message}`);
        } catch (err) {
          toast.error(`${providerLabel(p.provider)}: ${err.message}`);
        }
      }
      onSaved();
    } finally {
      setImporting(false);
    }
  };

  const add = async () => {
    setSaving(true);
    try {
      await createInboundNumber(form);
      toast.success("Number added");
      setForm(EMPTY_NUMBER);
      setAdding(false);
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  const update = async (id, body, message = "Number updated") => {
    try {
      await updateInboundNumber(id, body);
      toast.success(message);
      onSaved();
    } catch (err) {
      toast.error(err.message);
    }
  };
  const remove = async (n) => {
    if (!window.confirm(`Delete ${n.phoneNumber}?`)) return;
    try {
      await deleteInboundNumber(n.id);
      toast.success("Number deleted");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Card
      title="Inbound numbers"
      subtitle="Imported from the provider account when its credentials are saved — use &quot;Import from providers&quot; after buying another number. &quot;Preferred for&quot; only picks which number a user gets when you have several; it never hides the phone step (that is KYC Settings → step 4). With one number, it is used for everyone. To replace a number: switch off &quot;New codes&quot; on the old one, and switch off &quot;Receiving&quot; once its waiting codes have expired."
      actions={
        canCreate &&
        !adding && (
          <div className="flex items-center gap-2">
            {configured.length > 0 && (
              <button
                onClick={importAll}
                disabled={importing}
                className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center gap-1 disabled:opacity-40"
              >
                <FiRefreshCw size={12} className={importing ? "animate-spin" : ""} /> {importing ? "Importing…" : "Import from providers"}
              </button>
            )}
            <button onClick={() => setAdding(true)} className="px-3 py-2 text-xs font-semibold text-gray-500 hover:bg-gray-100 rounded-xl flex items-center gap-1">
              <FiPlus size={12} /> Add by hand
            </button>
          </div>
        )
      }
    >
      {adding && (
        <div className="border border-blue-100 bg-blue-50/30 rounded-xl p-3 grid md:grid-cols-4 gap-3 items-start">
          <select aria-label="Provider" className={inputCls} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
            {PROVIDERS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
          <input aria-label="Phone number" className={inputCls} placeholder="+14155550100" value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} />
          <input aria-label="Label" className={inputCls} placeholder="Label (optional)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setAdding(false)} className="px-3 py-2 text-xs text-gray-500 hover:bg-gray-100 rounded-xl">
              Cancel
            </button>
            <button onClick={add} disabled={saving || !form.phoneNumber.trim()} className={primaryBtn}>
              {saving ? "Adding…" : "Add"}
            </button>
          </div>
          <div className="md:col-span-4 text-xs">
            <div className="text-gray-500 mb-1">Preferred for (optional, only matters with several numbers; none = every country)</div>
            <CountryPicker value={form.countries} onChange={(countries) => setForm({ ...form, countries })} label="Number countries" />
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Number</th>
              <th className="text-left px-3 py-2">Provider</th>
              <th className="text-left px-3 py-2">Preferred for</th>
              <th className="text-center px-3 py-2">New codes</th>
              <th className="text-center px-3 py-2">Receiving</th>
              <th className="text-right px-3 py-2">Waiting</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {numbers.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-400">No numbers yet.</td>
              </tr>
            )}
            {numbers.map((n) => (
              <tr key={n.id} data-testid={`number-${n.id}`} className="align-top">
                <td className="px-3 py-2">
                  <div className="font-semibold text-gray-800">{n.phoneNumber}</div>
                  {n.label && <div className="text-[10px] text-gray-400">{n.label}</div>}
                </td>
                <td className="px-3 py-2">
                  {canEdit ? (
                    <select
                      aria-label={`${n.phoneNumber} provider`}
                      value={n.provider}
                      className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white"
                      onChange={(e) => {
                        if (window.confirm(`Move ${n.phoneNumber} to ${providerLabel(e.target.value)}? Do this only once the number has been ported.`)) {
                          update(n.id, { provider: e.target.value }, "Provider changed");
                        }
                      }}
                    >
                      {PROVIDERS.map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    providerLabel(n.provider)
                  )}
                </td>
                <td className="px-3 py-2 max-w-[280px]">
                  {editing === n.id ? (
                    <CountryPicker
                      value={n.countries}
                      label={`${n.phoneNumber} countries`}
                      onChange={(countries) => update(n.id, { countries }, "Countries updated")}
                    />
                  ) : (
                    <button
                      type="button"
                      disabled={!canEdit}
                      onClick={() => setEditing(n.id)}
                      className="text-left text-gray-600 hover:text-blue-600 disabled:hover:text-gray-600"
                    >
                      {n.countries.length ? n.countries.map(countryName).join(", ") : "Every country"}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  <Toggle
                    checked={n.acceptsNewChallenges}
                    disabled={!canEdit}
                    label={`${n.phoneNumber} new codes`}
                    onChange={(v) => update(n.id, { acceptsNewChallenges: v })}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <Toggle checked={n.isReceiving} disabled={!canEdit} label={`${n.phoneNumber} receiving`} onChange={(v) => update(n.id, { isReceiving: v })} />
                </td>
                <td className="px-3 py-2 text-right text-gray-600">{n.pendingChallenges}</td>
                <td className="px-3 py-2 text-right">
                  {editing === n.id && (
                    <button onClick={() => setEditing(null)} className="p-1.5 text-gray-400 hover:text-gray-700" aria-label="Done">
                      <FiX size={13} />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => remove(n)} className="p-1.5 text-gray-400 hover:text-red-600" aria-label={`Delete ${n.phoneNumber}`}>
                      <FiTrash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ------------------------------------------------------------------ countries

/** "ON only in India", "ON in every country except Bangladesh", … — the KYC step 4 setting in words. */
export const countriesSummary = ({ global, countries }) => {
  const names = countries.map(countryName).join(", ");
  if (global) return countries.length ? `ON in every country except ${names}` : "ON in every country";
  return countries.length ? `ON only in ${names}` : "OFF in every country";
};

/**
 * Which countries see the phone step — read-only here. It is KYC step 4, and
 * the KYC Settings page is the one place that changes it: editing it on two
 * pages made each page silently flip the other.
 */
const CountriesCard = ({ countries }) => {
  const { hasPermission } = usePermission();
  const on = countries.global || countries.countries.length > 0;
  return (
    <Card
      title="Countries"
      subtitle="Which users see the phone step, by their profile country. This is KYC step 4, set on the KYC Settings page (global default + country overrides)."
      actions={
        hasPermission("kyc-settings", "view") && (
          <a href="/kyc-settings" className="px-3 py-2 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl">
            Change in KYC Settings
          </a>
        )
      }
    >
      <div className="flex items-center gap-2 text-xs" data-testid="countries-summary">
        <span className="text-gray-500">Phone verification:</span>
        <span className={`font-semibold ${on ? "text-emerald-700" : "text-gray-500"}`}>{countriesSummary(countries)}</span>
      </div>
    </Card>
  );
};

// ------------------------------------------------------------------ limits

const LimitsCard = ({ config, canEdit, onSaved }) => {
  const initial = useMemo(() => Object.fromEntries(LIMIT_FIELDS.map((f) => [f.key, config.settings[f.key]])), [config]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = LIMIT_FIELDS.some((f) => Number(form[f.key]) !== Number(initial[f.key]));
  const invalid = LIMIT_FIELDS.find((f) => {
    const bound = config.limits[f.key];
    const v = Number(form[f.key]);
    return !Number.isInteger(v) || v < bound.min || v > bound.max;
  });

  const save = async () => {
    setSaving(true);
    try {
      await saveSmsSettings(Object.fromEntries(LIMIT_FIELDS.map((f) => [f.key, Number(form[f.key])])));
      toast.success("Limits saved");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const f = form;
  return (
    <Card
      title="Attempts and blocks"
      subtitle={`Each code counts as one try. A code that expires unused, is sent from another phone, or is wrong is a failed try. ${f.maxFailedAttempts} failed tries block the user for ${f.firstBlockHours}h, then ${f.repeatBlockHours}h each time${
        Number(f.maxBlocksBeforeReview) > 0 ? `; after ${f.maxBlocksBeforeReview} blocks only an admin can unblock` : ""
      }.`}
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {LIMIT_FIELDS.map((field) => {
          const bound = config.limits[field.key];
          return (
            <label key={field.key} className="space-y-1 text-xs">
              <span className="font-medium text-gray-600">{field.label}</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  aria-label={field.label}
                  className={inputCls}
                  min={bound.min}
                  max={bound.max}
                  value={form[field.key]}
                  disabled={!canEdit}
                  onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                />
                <span className="text-[10px] text-gray-400 w-12">{field.unit}</span>
              </div>
            </label>
          );
        })}
      </div>
      {canEdit && (
        <div className="flex items-center justify-end gap-3">
          {invalid && <span className="text-[11px] text-red-600">{invalid.label}: {config.limits[invalid.key].min}–{config.limits[invalid.key].max}</span>}
          <button className={primaryBtn} disabled={!dirty || !!invalid || saving} onClick={save}>
            {saving ? "Saving…" : "Save limits"}
          </button>
        </div>
      )}
    </Card>
  );
};

// ------------------------------------------------------------------ activity

const Pager = ({ pagination, onPage }) =>
  pagination.total_pages > 1 ? (
    <div className="flex items-center justify-end gap-2 text-xs text-gray-500">
      <button disabled={pagination.current_page <= 1} onClick={() => onPage(pagination.current_page - 1)} className="p-1.5 disabled:opacity-30" aria-label="Previous page">
        <FiChevronLeft />
      </button>
      {pagination.current_page} / {pagination.total_pages}
      <button disabled={pagination.current_page >= pagination.total_pages} onClick={() => onPage(pagination.current_page + 1)} className="p-1.5 disabled:opacity-30" aria-label="Next page">
        <FiChevronRight />
      </button>
    </div>
  ) : null;

const EVENT_BADGE = {
  processed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  ignored: "bg-gray-100 text-gray-600 border-gray-200",
};

const EventsCard = () => {
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWebhookEvents({ provider, status, page });
      setRows(res.data || []);
      setPagination(res.pagination || { current_page: 1, total_pages: 1, total: 0 });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [provider, status, page]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card
      title="Inbound SMS log"
      subtitle="Every message our numbers received. The SMS text is never stored and the sender is masked."
      actions={
        <div className="flex items-center gap-2">
          <select aria-label="Event provider" value={provider} onChange={(e) => { setProvider(e.target.value); setPage(1); }} className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs">
            <option value="">All providers</option>
            {PROVIDERS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <select aria-label="Event status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs">
            <option value="">All outcomes</option>
            <option value="processed">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="ignored">Ignored</option>
          </select>
          <button onClick={load} className="p-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50" aria-label="Refresh events">
            <FiRefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Received</th>
              <th className="text-left px-3 py-2">Provider</th>
              <th className="text-left px-3 py-2">From</th>
              <th className="text-left px-3 py-2">To</th>
              <th className="text-left px-3 py-2">Outcome</th>
              <th className="text-left px-3 py-2">User</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-gray-400">No messages yet.</td>
              </tr>
            )}
            {rows.map((e) => (
              <tr key={e.id}>
                <td className="px-3 py-2 text-gray-600">{formatDate(e.receivedAt)}</td>
                <td className="px-3 py-2">{providerLabel(e.provider)}</td>
                <td className="px-3 py-2 text-gray-600">{e.fromMasked || "—"}</td>
                <td className="px-3 py-2 text-gray-600">{e.toNumber || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${EVENT_BADGE[e.status] || ""}`}>{e.status}</span>
                  <span className="text-gray-500 ml-1">{reasonLabel(e.reason)}</span>
                </td>
                <td className="px-3 py-2">
                  {e.userId ? (
                    <a className="text-blue-600 hover:underline" href={`/user-full-details/${e.userId}`}>
                      #{e.userId}
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager pagination={pagination} onPage={setPage} />
    </Card>
  );
};

const UsersCard = () => {
  const [filter, setFilter] = useState("blocked");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchPhoneUsers({ filter, search, page });
      setRows(res.data || []);
      setPagination(res.pagination || { current_page: 1, total_pages: 1, total: 0 });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, search, page]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  return (
    <Card
      title="Users"
      actions={
        <div className="flex items-center gap-2">
          {[
            ["blocked", "Blocked"],
            ["review", "Need review"],
            ["verified", "Verified"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => { setFilter(key); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === key ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-100"}`}
            >
              {label}
            </button>
          ))}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Email, username, id" className="pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-xs w-48" />
          </div>
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Number</th>
              <th className="text-left px-3 py-2">Failed / blocks</th>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-gray-400">Nobody here.</td>
              </tr>
            )}
            {rows.map((u) => (
              <tr key={u.userId}>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-800">{u.username || `#${u.userId}`}</div>
                  <div className="text-[10px] text-gray-400">{u.email} · {u.country || "no country"}</div>
                </td>
                <td className="px-3 py-2 text-gray-600">{u.phoneNumber || "—"}</td>
                <td className="px-3 py-2 text-gray-600">{u.failedCount} / {u.blockCount}</td>
                <td className="px-3 py-2">
                  {u.review ? (
                    <span className="text-red-600 font-semibold">Needs review</span>
                  ) : u.blocked ? (
                    <span className="text-amber-600 font-semibold">Blocked {formatDuration(u.retryAfterSeconds)}</span>
                  ) : u.verifiedAt ? (
                    <span className="text-emerald-600 font-semibold">Verified</span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setOpenId(u.userId)} className="p-1.5 text-gray-400 hover:text-blue-600" aria-label={`Open user ${u.userId}`}>
                    <FiEye size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager pagination={pagination} onPage={setPage} />

      {openId && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setOpenId(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800">Phone verification — user #{openId}</h3>
              <button onClick={() => setOpenId(null)} className="p-1.5 text-gray-400 hover:text-gray-700" aria-label="Close">
                <FiX />
              </button>
            </div>
            <PhoneUserPanel userId={openId} onChanged={load} />
          </div>
        </div>
      )}
    </Card>
  );
};

// ------------------------------------------------------------------ page

const SmsVerification = () => {
  const { hasPermission, profile } = usePermission();
  const canEdit = hasPermission("sms-verification", "edit");
  const canCreate = hasPermission("sms-verification", "create");
  const canDelete = hasPermission("sms-verification", "delete");
  const isSuper = profile?.role === "superAdmin";

  const [tab, setTab] = useState("config");
  const [config, setConfig] = useState(null);
  // Bumped on every load: the editable cards are keyed on it, so they start
  // over from the saved values instead of copying props into state.
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchSmsConfig();
      setConfig(res.data);
      setVersion((v) => v + 1);
    } catch (err) {
      toast.error(err.message || "Could not load SMS verification settings");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-7xl mx-auto space-y-5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiMessageSquare className="text-blue-600" /> SMS Verification
                </h1>
                <p className="text-xs text-gray-400 mt-1">
                  KYC step 4. Users send a one-time code by SMS from their own phone to one of your numbers. Nothing here sends an SMS.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {[
                  ["config", "Configuration"],
                  ["activity", "Activity"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setTab(key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${tab === key ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-100"}`}
                  >
                    {label}
                  </button>
                ))}
                <button onClick={load} className="p-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50" aria-label="Refresh">
                  <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
              </div>
            </div>

            {!config && loading && <div className="text-xs text-gray-400">Loading…</div>}

            {config && tab === "config" && (
              <>
                {!config.encryptionKeyConfigured && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-3">
                    No encryption key on the server (JWT_SECRET missing, or SMS_CONFIG_ENCRYPTION_KEY malformed). Credentials cannot be saved and verification cannot be switched on until it is fixed.
                  </div>
                )}
                {config.encryptionKeyConfigured && config.encryptionKey?.weak && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-xl px-4 py-3" data-testid="weak-key-warning">
                    Credentials are encrypted with a key derived from JWT_SECRET, which is shorter than 32 characters. Use a longer JWT_SECRET (or set
                    SMS_CONFIG_ENCRYPTION_KEY). Changing it signs out all admins and the provider credentials must be saved again.
                  </div>
                )}
                <StatusCard config={config} canEdit={canEdit} onSaved={load} />
                <GeneralCard key={`general-${version}`} config={config} canEdit={canEdit} onSaved={load} />
                <Card title="Providers" subtitle="Credentials are stored encrypted and never shown again — only the last four characters. Paste the webhook URL into the provider's console for each number.">
                  <div className="grid lg:grid-cols-2 gap-4">
                    {config.providers.map((p) => (
                      <ProviderCard key={p.provider} provider={p} isSuper={isSuper} canEdit={canEdit} onSaved={load} />
                    ))}
                  </div>
                </Card>
                <NumbersCard numbers={config.numbers} providers={config.providers} canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} onSaved={load} />
                <CountriesCard countries={config.countries} />
                <LimitsCard key={`limits-${version}`} config={config} canEdit={canEdit} onSaved={load} />
              </>
            )}

            {config && tab === "activity" && (
              <>
                <UsersCard />
                <EventsCard />
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SmsVerification;
